import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useDestinatariByIds } from '@/hooks/useOrdini'
import { calcolaPesoColli } from '@/api/ordini'
import { toast } from 'sonner'
import { Calculator } from 'lucide-react'

/**
 * Step 4: Riepilogo e Conferma
 *
 * Mostra riepilogo completo della spedizione prima di crearla.
 * Al mount, se almeno un collo ha peso ancora al default 2.5,
 * chiama calcola-peso-colli e pre-compila i colli non modificati.
 */
export default function SpedizioneWizardStep4({ data, ordine, onSubmit, onBack, isSubmitting, onUpdateColli }) {
  const [isCalculating, setIsCalculating] = useState(false)
  const autoCalcDone = useRef(false)
  const destinatariById = useDestinatariByIds(data.colli?.map((collo) => collo.destinatario_id))

  const corrieriMap = {
    brt: 'BRT',
    gls: 'GLS',
    ups: 'UPS',
    dhl: 'DHL',
    fedex: 'FedEx',
    sda: 'SDA',
    tnt: 'TNT',
    poste: 'Poste Italiane',
    colauri: 'COLAURI',
    ritiro_in_sede: 'RITIRO IN SEDE',
    altro: 'Altro',
  }

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100)
  }

  const getRigaDescrizione = (rigaId) => {
    const riga = ordine.righe.find(r => r.id === rigaId)
    return riga ? riga.descrizione : `Riga #${rigaId}`
  }

  const getDestinatarioNome = (destinatarioId) => {
    const destinatario = destinatariById.get(Number(destinatarioId))
    return destinatario ? destinatario.nome_completo : `ID ${destinatarioId}`
  }

  const pesoTotale = data.colli.reduce((sum, collo) => sum + parseFloat(collo.peso_kg || 0), 0)
  const valoreTotale = data.colli.reduce((sum, collo) => sum + (collo.valore_dichiarato_cents || 0), 0)

  // Peso "da calcolare" = vuoto, null, 0 o il vecchio default 2.5
  const isDefaultPeso = (peso) => {
    if (peso === '' || peso === null || peso === undefined) return true
    const n = parseFloat(peso)
    if (isNaN(n) || n === 0) return true
    if (n === 2.5) return true
    return false
  }

  const hasPesoMancante = data.colli.some(c => !parseFloat(c.peso_kg) || parseFloat(c.peso_kg) <= 0)

  const chiamaCalcolo = async (overrideTutti = false) => {
    if (!onUpdateColli || !ordine?.id) return
    setIsCalculating(true)
    try {
      const payload = {
        ordine_id: ordine.id,
        colli: data.colli.map((c, i) => ({
          index: i,
          righe_ordine: (c.righe_ordine || []).map(r => ({
            riga_ordine_id: r.riga_ordine_id,
            quantita: r.quantita,
          })),
        })),
      }
      const res = await calcolaPesoColli(payload)
      const suggeriti = res.data.colli || []
      const warnings = res.data.warnings || []

      let aggiornati = 0
      const newColli = data.colli.map((collo, idx) => {
        const sugg = suggeriti.find(s => s.index === idx)
        if (!sugg) return collo
        const pesoSugg = parseFloat(sugg.peso_suggerito_kg)
        if (pesoSugg <= 0) return collo
        if (!overrideTutti && !isDefaultPeso(collo.peso_kg)) return collo
        aggiornati++
        return { ...collo, peso_kg: sugg.peso_suggerito_kg }
      })

      if (aggiornati > 0) {
        onUpdateColli(newColli)
        toast.success(`Peso aggiornato per ${aggiornati} collo${aggiornati > 1 ? 'i' : ''}`)
      } else if (overrideTutti) {
        toast.info('Nessun peso suggerito valido (prodotti senza peso configurato?)')
      }

      warnings.forEach(w => toast.warning(w))
    } catch (err) {
      toast.error('Errore nel calcolo peso: ' + (err?.response?.data?.detail || err.message))
    } finally {
      setIsCalculating(false)
    }
  }

  // Auto-calcolo al primo mount se almeno un collo e' al default
  useEffect(() => {
    if (autoCalcDone.current) return
    if (!data.colli || data.colli.length === 0) return
    const hasDefault = data.colli.some(c => isDefaultPeso(c.peso_kg))
    if (!hasDefault) return
    autoCalcDone.current = true
    chiamaCalcolo(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRicalcola = async () => {
    const hasModifiche = data.colli.some(c => !isDefaultPeso(c.peso_kg))
    if (hasModifiche && !confirm('Alcuni pesi sono stati modificati manualmente. Sovrascrivere tutti con il peso calcolato dai contenuti?')) {
      return
    }
    await chiamaCalcolo(true)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Riepilogo Spedizione</CardTitle>
          <p className="text-sm text-muted-foreground">
            Verifica i dati prima di creare la spedizione
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dati Spedizione */}
          <div>
            <h3 className="font-semibold mb-3">Dati Spedizione</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Corriere:</span>
                <span className="ml-2 font-medium">{corrieriMap[data.corriere]}</span>
              </div>
              {data.tracking_number && (
                <div>
                  <span className="text-muted-foreground">Tracking:</span>
                  <span className="ml-2 font-mono">{data.tracking_number}</span>
                </div>
              )}
              <div className="col-span-2">
                <span className="text-muted-foreground">{data.corriere === 'ritiro_in_sede' ? 'Indirizzo di ritiro:' : 'Indirizzo:'}</span>
                <div className="ml-2 font-medium">
                  {data.indirizzo_via} {data.indirizzo_civico}
                  <br />
                  {data.indirizzo_cap} {data.indirizzo_citta} ({data.indirizzo_provincia})
                  {data.indirizzo_presso && (
                    <>
                      <br />
                      Presso: {data.indirizzo_presso}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Statistiche */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{data.colli.length}</div>
                <p className="text-xs text-muted-foreground">Colli Totali</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{pesoTotale.toFixed(2)} kg</div>
                <p className="text-xs text-muted-foreground">Peso Totale</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{formatCurrency(valoreTotale)}</div>
                <p className="text-xs text-muted-foreground">Valore Dichiarato</p>
              </CardContent>
            </Card>
          </div>

          <Separator />

          {/* Dettaglio Colli */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Dettaglio Colli</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRicalcola}
                disabled={isCalculating || isSubmitting}
              >
                <Calculator className="mr-2 h-4 w-4" />
                {isCalculating ? 'Calcolo...' : 'Ricalcola peso dai contenuti'}
              </Button>
            </div>
            <div className="space-y-4">
              {data.colli.map((collo, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        Collo {collo.numero_collo}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{collo.peso_kg} kg</Badge>
                        {collo.valore_dichiarato_cents > 0 && (
                          <Badge variant="secondary">
                            {formatCurrency(collo.valore_dichiarato_cents)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Destinatario */}
                    <div className="text-sm">
                      <span className="text-muted-foreground">Destinatario:</span>
                      <span className="ml-2 font-medium">{getDestinatarioNome(collo.destinatario_id)}</span>
                    </div>

                    {/* Dimensioni */}
                    {collo.lunghezza_cm && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Dimensioni:</span>
                        <span className="ml-2">
                          {collo.lunghezza_cm} × {collo.larghezza_cm} × {collo.altezza_cm} cm
                        </span>
                      </div>
                    )}

                    {/* Contenuto */}
                    {collo.righe_ordine && collo.righe_ordine.length > 0 && (
                      <div>
                        <div className="text-sm text-muted-foreground mb-2">Contenuto:</div>
                        <div className="space-y-1">
                          {collo.righe_ordine.map((assegnazione, rigaIdx) => (
                            <div
                              key={rigaIdx}
                              className="flex items-center justify-between text-sm bg-muted/50 rounded px-3 py-2"
                            >
                              <span>{getRigaDescrizione(assegnazione.riga_ordine_id)}</span>
                              <Badge variant="outline">×{assegnazione.quantita}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Note */}
                    {collo.note && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Note:</span>
                        <span className="ml-2 italic">{collo.note}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Note Spedizione */}
          {data.note && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-2">Note Spedizione</h3>
                <p className="text-sm text-muted-foreground italic">{data.note}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Warning peso mancante */}
      {hasPesoMancante && !isCalculating && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-destructive">
          Uno o più colli hanno peso non valido o mancante. Torna allo Step 2 per inserirlo manualmente
          oppure clicca "Ricalcola peso dai contenuti" se i prodotti hanno peso configurato.
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
          Indietro
        </Button>
        <Button
          type="button"
          onClick={() => onSubmit(data)}
          disabled={isSubmitting || hasPesoMancante || isCalculating}
          className="bg-green-600 hover:bg-green-700"
        >
          {isSubmitting ? 'Creazione in corso...' : 'Crea Spedizione'}
        </Button>
      </div>
    </div>
  )
}
